/**
 * Duplicate Sales — inspector tool page.
 *
 * Thin fetcher → DataViewTable (same shape as the product-duplicates
 * tab). Detection lives in the ``ex-sale-ls-duplicate`` tagger and is
 * surfaced by `/inspector/sales/tools/duplicate-sales/{fp_code}` as one
 * row per detector group.
 */
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'

import { useInspector } from '../../../../contexts/InspectorContext'
import { useInspectorRef } from '../../../../hooks/useInspectorRef'
import { inspectorApi } from '../../../../services/api'
import type { DuplicateSaleExceptionRow } from '../../../../services/types/sales'
import { DataViewTable } from '../../../../components/shared/DataViewTable'
import { duplicateSaleConfig } from '../../../../config/exceptions/sales/duplicateSaleExceptions'

const DuplicateSales: React.FC = () => {
  const { fpCode: fp_code } = useInspectorRef()
  const { franchiseData } = useInspector()
  const [rows, setRows] = useState<DuplicateSaleExceptionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Bumped by the table's onAfterMutation (e.g. an ignore applied in the
  // Compare Duplicates dialog) so closed groups drop from the list.
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!fp_code) {
      setError('Missing franchise partner code')
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    inspectorApi
      .getDuplicateSales(fp_code)
      .then((res) => {
        if (!cancelled) setRows(res.data.inventory || [])
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              'Failed to fetch duplicate sales',
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fp_code, refreshKey])

  const franchiseName = franchiseData?.franchise_name || 'Loading...'
  const pageTitle = `Duplicate Sales Checker | ${franchiseName} | Inspector Tool`

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Typography variant="h4" component="h1">
            Duplicate Sales Checker
          </Typography>
          <Chip
            label="BETA"
            size="small"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              height: 24,
              bgcolor: 'warning.main',
              color: '#fff',
            }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Groups of sales at the same outlet that may be the same
          transaction recorded more than once - matching line items and
          amount, with the same customer, a matching service-job identity
          or a very close timestamp. Usually a sync replay or an operator
          pressing enter twice. Scores are a confidence estimate, not a
          verdict - expand a group to compare the invoices side-by-side
          before voiding anything in Lightspeed.
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && rows.length === 0 && (
          <Alert severity="success">
            No duplicate sales detected for this franchise partner.
          </Alert>
        )}

        {!loading && !error && rows.length > 0 && (
          <DataViewTable
            config={duplicateSaleConfig}
            data={rows as unknown as Record<string, any>[]}
            variant="inspector"
            fpCode={fp_code}
            onAfterMutation={() => setRefreshKey((k) => k + 1)}
          />
        )}
      </Box>
    </>
  )
}

export default DuplicateSales
